import React from "react";
import i18n from "i18n";
import { sendPerPeriodItem } from "pages/Dashboards/AdministrativeDashboard/types";

export const getReportsSentDataForPieChart = (
  TotalSendByPeriodPercentage: number
) => [
  {
    name: "sent",
    value: TotalSendByPeriodPercentage,
    fill: "#040b44",
  },
  {
    name: "notSent",
    value: 100 - TotalSendByPeriodPercentage,
    fill: "#e6e8ef",
  },
];

export const periodStrings = {
  weekly: i18n.t("thisWeek"),
  monthly: i18n.t("thisMonth"),
  perYear: i18n.t("thisYear"),
};

export const controllersStrings = {
  controlCenter: i18n.t("controlCenter"),
  regularController: i18n.t("regularControllers"),
  seniorController: i18n.t("seniorControllers"),
};

export const periodAverageStrings = {
  weekly: i18n.t("weeklyAverage"),
  monthly: i18n.t("monthlyAverage"),
  perYear: i18n.t("yearlyAverage"),
};

export const getMinimalAreaChartData = (dataArray: number[]) => {
  return dataArray.map((num, index) => {
    return {
      name: index.toString(),
      amount: num,
    };
  });
};

export const getChartLineColor = (controllerType: string) => {
  const gradients: Record<string, string[]> = {
    controlCenter: [
      "rgba(40,57,153,1)",
      "rgba(72,90,181,1)",
      "rgba(59,90,255,1)",
      "rgba(112,132,237,1)",
      "rgba(149,164,241,1)",
      "rgba(177,189,251,1)",
    ],
    regularController: [
      "rgba(4,11,68,1)",
      "rgba(4,11,68,1)",
      "rgba(40,124,137,1)",
      "rgba(50,157,157,1)",
      "rgba(63,202,184,1)",
      "rgba(80,254,216,1)",
    ],
    seniorController: [
      "rgba(106,49,82,1)",
      "rgba(123,49,78,1)",
      "rgba(150,73,97,1)",
      "rgba(177,84,102,1)",
      "rgba(210,72,82,1)",
      "rgba(252,99,100,1)",
    ],
  };

  const colors = gradients[controllerType];

  return (
    <linearGradient id={controllerType}>
      {colors.map((color, index) => (
        <stop
          key={`controllerType-${index.toString()}`}
          offset={`${(index * 100) / (colors.length - 1)}%`}
          stopColor={color}
        />
      ))}
    </linearGradient>
  );
};

export const getReportsCompletedPerControllerForBarChart =
  (reportCompletedPerControlCenter: { [key: string]: number }) => {
    const dataArray = Object.keys(reportCompletedPerControlCenter).map(
      (key) => {
        const item = reportCompletedPerControlCenter[key];
        return {
          name: key,
          amount: item,
        };
      }
    );
    return dataArray;
  };

// The backend returns Hebrew X-axis labels: day names (weekly view) and
// "שבוע N" (monthly view). Map them to English for display. Numeric yearly
// labels ("1".."12") are language-neutral and pass through unchanged.
const hebrewDayToEnglish: Record<string, string> = {
  ראשון: "Sunday",
  שני: "Monday",
  שלישי: "Tuesday",
  רביעי: "Wednesday",
  חמישי: "Thursday",
  שישי: "Friday",
  שבת: "Saturday",
};

export const translatePeriodLabel = (key: string): string => {
  if (hebrewDayToEnglish[key]) {
    return hebrewDayToEnglish[key];
  }
  const weekMatch = key.match(/^שבוע\s+(\d+)$/);
  if (weekMatch) {
    return `Week ${weekMatch[1]}`;
  }
  return key;
};

export const getReportsSentDataForAreaChart = (sendPerPeriod: {
  [key: string]: sendPerPeriodItem;
}) => {
  const dataArray = Object.keys(sendPerPeriod).map((key) => {
    const item = sendPerPeriod[key];
    return {
      name: translatePeriodLabel(key),
      amount: item.send,
      percentage: item.percentage,
    };
  });
  return dataArray;
};

export const getYAxisDataForAreaChart = (period: string) => {
  const isYearPeriod = period === "perYear";
  const yAxisTicks = !isYearPeriod
    ? [0, 500, 1000, 1500, 2000]
    : [0, 5000, 10000, 15000, 20000, 25000];
  const maxY = !isYearPeriod ? 2000 : 25000;
  return { yAxisTicks, maxY };
};

export const getYAxisDataForBarChart = (period: string) => {
  let yAxisTicks;
  let maxY;
  switch (period) {
    case "weekly":
      yAxisTicks = [0, 125, 250, 375, 500, 625, 750];
      maxY = 750;
      break;
    case "monthly":
      yAxisTicks = [0, 500, 1000, 1500, 2000, 2500, 3000];
      maxY = 3000;
      break;
    case "perYear":
      yAxisTicks = [0, 6000, 12000, 18000, 24000, 30000, 36000];
      maxY = 36000;
      break;

    default:
      break;
  }
  return { yAxisTicks, maxY };
};
